
import React, { useEffect, useState } from 'react';
import StepsPanel from './StepsPanel.jsx';
import { useOnboardingQuery } from '../../lib/Queries';
import Loading from '../Loading';
import { getBusinessId } from '~/lib/Api';
import useBusinessNavigate from '../../lib/useBusinessNavigate.js';
import RightCaretIcon from '~/components/icons/RightCaretIcon';
import { trackAmplitudeEvent } from '~/lib/AmplitudeTracking';

const SetupSteps = ({ openAiChat, className = "", setIsVisible }) => {
    const { data, isLoading } = useOnboardingQuery();
    const navigate = useBusinessNavigate();

    const [showStepsPanel, setShowStepsPanel] = useState(false);
    const [selectedStepIndex, setSelectedStepIndex] = useState(null);

    const steps = Array.isArray(data) ? data : [];

    const totalSteps = steps.length;
    const stepsCompleted = steps.filter(s => !!s.completed).length;;
    const degreesDone = (stepsCompleted / totalSteps) * 360;

    const dedupingKey = `breely_ai_chat_opened-${getBusinessId()}`;

    useEffect(() => {
        // Open up the nav if it's a first load
        if (
            totalSteps > 0 &&
            stepsCompleted === 0
            //&& !localStorage.getItem(SETUP_STEPS_KEY)
            && !sessionStorage.getItem(dedupingKey)
        ) {
            openAiChat(true);
            sessionStorage.setItem(dedupingKey, '1');
            //setShowStepsPanel(true);
        }
    }, [data?.length]);

    const isVisible = !(totalSteps === 0 || stepsCompleted >= totalSteps);

    useEffect(() => {
        if (setIsVisible) setIsVisible(isVisible);
    }, [isVisible]);

    if (!isVisible) {
        // All done!
        return null;
    }

    // Find the next incomplete step and its index among all steps
    const defaultStepIndex = steps.findIndex(s => !s.completed);

    const displayIndex = selectedStepIndex !== null ? selectedStepIndex : defaultStepIndex;
    const displayStep = displayIndex >= 0 ? steps[displayIndex] : null;
    const currentStepNumber = displayIndex + 1;

    if (!displayStep) return null;

    const goToStep = () => {
        trackAmplitudeEvent('Setup Step Button Clicked', {
            step_key: displayStep.key,
            step_title: displayStep.title,
            step_button: displayStep.button,
            step_href: displayStep.href,
            step_number: currentStepNumber,
            steps_completed: stepsCompleted,
            total_steps: totalSteps,
        });
        navigate(displayStep.href);
    };

    return (
        <>
            <div
                className={`-mx-3 w-[calc(100%+24px)] mt-2 sm:mt-0 sm:w-auto sm:mx-0 border border-[#E3E8F0] bg-gradient-to-br from-[#66748D] to-[#939DAF] text-white sm:rounded-2xl leading-none p-3 xl:p-5 transition-colors ${className}`}
            >
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                    {/* MOBILE */}
                    <div className="flex gap-2 xl:hidden">
                        <div className="w-[32px] h-[32px]">
                            <button
                                className="form-progress xl:hidden"
                                style={{
                                    background: `conic-gradient(#E3E8F0 ${degreesDone}deg, #939DAF 0deg)`,
                                }}
                                onClick={() => setShowStepsPanel(true)}
                            >
                                <div className="form-progress-inner text-xs flex items-center justify-center !bg-[#66748D] text-[#E3E8F0]">
                                    {stepsCompleted}/{totalSteps}
                                </div>
                            </button>
                        </div>
                        <div className="leading-none">
                            <div className="leading-none text-sm xl:text-xs font-semibold xl:uppercase">
                                Get Started
                            </div>
                            <p className="text-sm  mb-1 sm:hidden">
                                {displayStep.description}
                            </p>
                            <button
                                type="button"
                                className="transition-colors duration-200 text-sm sm:font-medium sm:text-xs leading-none text-white underline xl:text-sm xl:px-6 xl:py-3 xl:border xl:rounded-full xl:hover:bg-primary-100 xl:w-full"
                                onClick={goToStep}
                            >
                                {displayStep.button}
                            </button>
                        </div>
                        <div className="self-center ml-auto sm:hidden">
                                <RightCaretIcon />
                        </div>
                    </div>

                    {/* DESKTOP */}
                    <div className="hidden xl:block">
                        <div className="flex items-center justify-between gap-3 leading-none mb-4">
                            <p className="leading-none text-xs font-semibold uppercase text-[#e9ebef]">
                                Get Started
                            </p>
                            <button
                                type="button"
                                className="text-xs"
                                onClick={() => setShowStepsPanel(true)}
                            >
                                {stepsCompleted}/{totalSteps}
                            </button>
                        </div>

                        <p className="mb-1 font-semibold leading-none">
                            {displayStep.title}
                        </p>
                        <p className="text-sm mb-3">
                            {displayStep.description}
                        </p>
                        <button
                            type="button"
                            className="rounded-lg px-6 py-3 bg-white hover:bg-opacity-90 text-[#66748D] font-medium text-sm leading-none w-full"
                            onClick={goToStep}
                        >
                            {displayStep.button}
                        </button>

                        <div className="justify-center gap-1.5 mt-4 hidden xl:flex">
                            {steps.map((step, i) => (
                                <button
                                    type="button"
                                    key={step.key}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        i === displayIndex
                                            ? 'outline outline-1 outline-[#fff] outline-offset-1' : ''}
                                            ${step.completed
                                                ? 'bg-white/80'
                                                : 'bg-white/10 completed'
                                    }`}
                                    onClick={() => setSelectedStepIndex(i)}
                                />
                            ))}
                        </div>
                    </div>
                    </>
                )}
            </div>

            <StepsPanel
                isOpen={showStepsPanel}
                onClose={() => setShowStepsPanel(false)}
                openAiChat={openAiChat}
            />
        </>
    );
};

export default SetupSteps;
